/**
 * /workspace/catalog/bulk — drag-drop CSV importer with preview + commit.
 */
import React from "react";
import { useMutation } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { UploadCloud, FileSpreadsheet, CheckCircle2, XCircle, Loader2, Rocket, Download } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const FIELDS = ["title", "price", "category", "sku", "description", "stock_on_hand", "cost_price"] as const;

function parseCSV(text: string): { headers: string[]; rows: string[][] } {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length === 0) return { headers: [], rows: [] };
  const split = (line: string) => {
    // Minimal CSV split — respects double-quoted values.
    const out: string[] = []; let cur = ""; let q = false;
    for (const ch of line) {
      if (ch === '"') { q = !q; continue; }
      if (ch === "," && !q) { out.push(cur); cur = ""; continue; }
      cur += ch;
    }
    out.push(cur);
    return out.map((s) => s.trim());
  };
  const headers = split(lines[0]).map((h) => h.toLowerCase());
  const rows = lines.slice(1).map(split);
  return { headers, rows };
}

export default function CatalogBulk() {
  const [file, setFile] = React.useState<File | null>(null);
  const [headers, setHeaders] = React.useState<string[]>([]);
  const [rows, setRows] = React.useState<string[][]>([]);
  const [mapping, setMapping] = React.useState<Record<string, string>>({});
  const [preview, setPreview] = React.useState<any>(null);
  const nav = useNavigate();

  const onFile = React.useCallback(async (f: File) => {
    if (!f.name.toLowerCase().endsWith(".csv")) { toast.error("Please drop a .csv file"); return; }
    setFile(f);
    setPreview(null);
    const text = await f.text();
    const p = parseCSV(text);
    setHeaders(p.headers);
    setRows(p.rows);
    // Auto-map by header name if it matches a known field.
    const auto: Record<string, string> = {};
    for (const field of FIELDS) {
      const hit = p.headers.find((h) => h === field || h.replace(/[_\s]/g, "") === field.replace(/_/g, ""));
      if (hit) auto[field] = hit;
    }
    setMapping(auto);
  }, []);

  const buildRows = () => {
    const idx: Record<string, number> = {};
    for (const f of FIELDS) { const h = mapping[f]; if (h) idx[f] = headers.indexOf(h); }
    return rows.map((r) => ({
      title:         idx.title >= 0 ? r[idx.title] : undefined,
      price:         idx.price >= 0 ? Number(r[idx.price]) : undefined,
      category:      idx.category >= 0 ? r[idx.category] : undefined,
      sku:           idx.sku >= 0 ? r[idx.sku] : undefined,
      description:   idx.description >= 0 ? r[idx.description] : undefined,
      stock_on_hand: idx.stock_on_hand >= 0 ? Number(r[idx.stock_on_hand]) : undefined,
      cost_price:    idx.cost_price >= 0 ? Number(r[idx.cost_price]) : undefined,
    }));
  };

  const validate = useMutation({
    mutationFn: async () => (await api.post("/workspace/catalog/bulk/preview", { rows: buildRows() })).data,
    onSuccess: (d) => setPreview(d),
    onError: (err: any) => toast.error(err?.response?.data?.detail || "Validate failed"),
  });

  const commit = useMutation({
    mutationFn: async () => (await api.post("/workspace/catalog/bulk/commit", { rows: buildRows(), only_valid: true })).data,
    onSuccess: (d) => {
      toast.success(`✅ ${d.written} products written · ${d.skipped} skipped`);
      nav("/workspace/sell/inventory");
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail || "Commit failed"),
  });

  const [dragging, setDragging] = React.useState(false);

  return (
    <DashboardLayout title="Bulk import" subtitle="Drop a CSV, map columns, validate, launch. Rollback safe.">
      {/* Dropzone */}
      <div
        data-testid="bulk-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files?.[0]; if (f) onFile(f); }}
        className={`rounded-2xl border-2 border-dashed p-10 text-center transition-colors ${dragging ? "border-[color:var(--sb-saffron,#F97316)] bg-[color:var(--sb-saffron,#F97316)]/5" : "border-[color:var(--sg-border)] bg-[color:var(--sg-card)]"}`}
      >
        <UploadCloud className={`h-10 w-10 mx-auto mb-2 ${dragging ? "text-[color:var(--sb-saffron,#F97316)]" : "text-[color:var(--sg-muted)]"}`}/>
        <div className="font-serif-display text-lg font-bold text-[color:var(--sg-fg)]">
          {file ? file.name : "Drop your CSV here"}
        </div>
        <div className="text-xs text-[color:var(--sg-muted)] mt-1">
          Max 1,000 rows · Headers detected: <b>{headers.length || "—"}</b> · Rows: <b>{rows.length || "—"}</b>
        </div>
        <div className="mt-3 flex items-center justify-center gap-2">
          <label className="inline-flex items-center h-9 px-4 rounded-full bg-[color:var(--sb-saffron,#F97316)] hover:bg-[color:var(--sb-saffron-hover,#EA580C)] text-white font-semibold text-sm cursor-pointer" data-testid="bulk-file-input-label">
            Choose file
            <input type="file" accept=".csv" className="hidden" onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} data-testid="bulk-file-input"/>
          </label>
          <a
            data-testid="bulk-template"
            href="data:text/csv,title,price,category,sku,description,stock_on_hand,cost_price%0AKurta,999,apparel,SB-001,Saffron block-print,25,450"
            download="socibaba_import_template.csv"
            className="inline-flex items-center gap-1 h-9 px-3 rounded-full border border-[color:var(--sg-border)] text-xs text-[color:var(--sg-muted)] hover:text-[color:var(--sg-fg)]"
          >
            <Download className="h-3.5 w-3.5"/> Template
          </a>
        </div>
      </div>

      {headers.length > 0 && (
        <div className="mt-6 rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-5" data-testid="bulk-mapper">
          <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[color:var(--sg-muted)] mb-3 flex items-center gap-1.5"><FileSpreadsheet className="h-3 w-3"/> Map columns</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {FIELDS.map((f) => (
              <div key={f}>
                <label className="text-[10px] uppercase tracking-widest font-semibold text-[color:var(--sg-muted)]">{f}{["title","price"].includes(f) && <span className="text-rose-500"> *</span>}</label>
                <select
                  data-testid={`bulk-map-${f}`}
                  value={mapping[f] || ""}
                  onChange={(e) => setMapping({ ...mapping, [f]: e.target.value })}
                  className="mt-1 h-9 w-full rounded-md border border-[color:var(--sg-border)] bg-transparent px-2 text-sm"
                >
                  <option value="">— skip —</option>
                  {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-2">
            <Button
              data-testid="bulk-validate"
              onClick={() => validate.mutate()}
              disabled={validate.isPending || !mapping.title || !mapping.price}
              className="h-10"
            >
              {validate.isPending ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin"/> Validating…</> : "Validate rows"}
            </Button>
            {preview && (
              <Button
                data-testid="bulk-commit"
                onClick={() => commit.mutate()}
                disabled={commit.isPending || preview.valid === 0}
                className="h-10 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
              >
                {commit.isPending ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin"/> Writing…</> : <><Rocket className="h-4 w-4 mr-1.5"/> Import {preview.valid} valid rows</>}
              </Button>
            )}
          </div>
        </div>
      )}

      {preview && (
        <div className="mt-6 rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-5" data-testid="bulk-preview">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[color:var(--sg-muted)]">Preview</div>
            <div className="text-xs">
              <span className="text-emerald-700 dark:text-emerald-300 font-semibold">{preview.valid} valid</span> ·{" "}
              <span className="text-rose-700 dark:text-rose-300 font-semibold">{preview.invalid} invalid</span>
            </div>
          </div>
          <div className="overflow-x-auto sg-scrollbar-thin max-h-[380px]">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-[color:var(--sg-card)]">
                <tr className="text-[10px] uppercase tracking-widest text-[color:var(--sg-muted)]">
                  <th className="text-left py-1.5 pl-2">Row</th>
                  <th className="text-left py-1.5">Status</th>
                  <th className="text-left py-1.5">Title</th>
                  <th className="text-right py-1.5">Price</th>
                  <th className="text-left py-1.5 pr-2">Errors</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((r: any) => (
                  <tr key={r.row} className="border-t border-[color:var(--sg-border)]/60">
                    <td className="py-1.5 pl-2 font-mono text-[10px]">{r.row}</td>
                    <td className="py-1.5">{r.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 inline"/> : <XCircle className="h-3.5 w-3.5 text-rose-500 inline"/>}</td>
                    <td className="py-1.5 truncate max-w-[240px]">{r.data.title || "—"}</td>
                    <td className="py-1.5 text-right tabular-nums">{r.data.price != null ? `₹${Number(r.data.price).toLocaleString("en-IN")}` : "—"}</td>
                    <td className="py-1.5 pr-2 text-rose-700 dark:text-rose-300">{r.errors.join(", ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
